<template>
  <div class="extracted-details">
    <div v-for="section in sections" :key="section.title" class="details-section">
      <h3 class="section-title">
        <i :class="section.icon"></i>
        {{ section.title }}
      </h3>
      
      <!-- Info Grid for simple key-value pairs -->
      <div v-if="section.type === 'info-grid'" class="info-grid">
        <div 
          v-for="field in section.fields" 
          :key="field.key"
          class="info-item"
        >
          <span class="">{{ field.label }}:</span>
          <span class="value">{{ formatValue(getNestedValue(data, field.key), field.format) }}</span>
        </div>
      </div>

      <!-- Summary Cards -->
      <div v-else-if="section.type === 'summary-grid'" class="summary-grid">
        <div 
          v-for="card in section.cards" 
          :key="card.key"
          class="summary-card"
          :class="{ 'highlight': card.highlight }"
        >
          <div class="summary-label">{{ card.label }}</div>
          <div class="summary-value">{{ formatValue(getNestedValue(data, card.key), card.format) }}</div>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else-if="section.type === 'table'" class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th v-for="column in section.columns" :key="column.key">
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in section.data" :key="index">
              <td v-for="column in section.columns" :key="column.key">
                <span 
                  v-if="column.type === 'badge'"
                  class="rate-badge"
                  :class="getBadgeClass(row[column.key], column.badgeClass)"
                >
                  {{ formatValue(row[column.key], column.format) }}
                </span>
                <span v-else>
                  {{ formatValue(row[column.key], column.format) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- List Items -->
      <div v-else-if="section.type === 'list'" class="list-container">
        <div 
          v-for="(item, index) in section.items" 
          :key="index" 
          class="list-item"
        >
          <div 
            v-for="field in section.fields" 
            :key="field.key"
            class="list-field"
            :class="field.class"
          >
            {{ formatValue(item[field.key], field.format) }}
          </div>
        </div>
      </div>

      <!-- Credit Facilities Display -->
      <div v-else-if="section.type === 'credit-facilities'" class="credit-facilities-container">
        <div 
          v-for="(facility, index) in section.data" 
          :key="index"
          class="credit-facility-card"
        >
          <div class="facility-header">
            <h4 class="facility-title">
              <i class="las la-credit-card"></i>
              {{ facility.provider }} - {{ facility.dp_contract_no }}
            </h4>
            <span class="facility-status" :class="getFacilityStatusClass(facility)">
              {{ getFacilityStatus(facility) }}
            </span>
          </div>
          
          <div class="facility-details">
            <div class="details-grid">
              <div 
                v-for="field in section.fields" 
                :key="field.key"
                class="detail-item"
              >
                <span class="detail-label">{{ field.label }}</span>
                <span class="detail-value">{{ formatValue(getNestedValue(facility, field.key), field.format) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Credit Applications Display -->
      <div v-else-if="section.type === 'credit-applications'" class="credit-applications-container">
        <div 
          v-for="(application, index) in section.data" 
          :key="index"
          class="credit-application-card"
        >
          <div class="application-header">
            <h4 class="application-title">
              <i class="las la-file-invoice"></i>
              {{ application.provider }} - {{ application.dp_application_no }}
            </h4>
            <span class="application-phase" :class="getApplicationPhaseClass(application)">
              {{ application.phase }}
            </span>
          </div>
          
          <div class="application-details">
            <div class="details-grid">
              <div 
                v-for="field in section.fields" 
                :key="field.key"
                class="detail-item"
              >
                <span class="detail-label">{{ field.label }}</span>
                <span class="detail-value">{{ formatValue(getNestedValue(application, field.key), field.format) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notes/Text Content -->
      <div v-else-if="section.type === 'text'" class="text-content">
        {{ section.content }}
      </div>
    </div>

    <!-- No Data Message -->
    <div v-if="!hasData" class="no-data">
      <div class="no-data-icon">
        <i class="las la-file-alt"></i>
      </div>
      <h3>No Extracted Data</h3>
      <p>No extracted details are available for this file type.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseExtractedDetails',
  props: {
    sections: {
      type: Array,
      required: true
    },
    hasData: {
      type: Boolean,
      default: true
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    },

    formatValue(value, format) {
      if (value === null || value === undefined || value === '') return 'N/A';
      
      switch (format) {
        case 'currency':
          return this.formatCurrency(value);
        case 'date':
          return this.formatDate(value);
        case 'dateRange':
          return this.formatDateRange(value.start, value.end);
        case 'rate':
          return this.formatRate(value);
        case 'percentage':
          return this.formatPercentage(value);
        default:
          return value;
      }
    },

    formatCurrency(amount) {
      if (!amount || amount === '') return 'N/A';
      
      // Handle comma-separated numbers by removing commas before parsing
      const cleanAmount = typeof amount === 'string' ? amount.replace(/,/g, '') : amount;
      const numAmount = parseFloat(cleanAmount);
      
      if (isNaN(numAmount)) return amount;
      
      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2
      }).format(numAmount);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('en-AE');
      } catch (error) {
        return dateString;
      }
    },

    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return 'N/A';
      return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    },

    formatRate(rate) {
      if (rate === 'reverse_charge') return 'Reverse Charge';
      if (rate === '0') return '0%';
      return `${rate}%`;
    },

    formatPercentage(value) {
      if (!value) return 'N/A';
      return `${value}%`;
    },

    getBadgeClass(value, badgeClassFunction) {
      if (typeof badgeClassFunction === 'function') {
        return badgeClassFunction(value);
      }
      return badgeClassFunction || '';
    },

    getFacilityStatus(facility) {
      if (facility.closed_date && facility.closed_date !== 'N/A') {
        return 'Closed';
      }
      return 'Active';
    },

    getFacilityStatusClass(facility) {
      if (facility.closed_date && facility.closed_date !== 'N/A') {
        return 'status-closed';
      }
      return 'status-active';
    },

    getApplicationPhaseClass(application) {
      const phase = application.phase ? application.phase.toLowerCase() : '';
      switch (phase) {
        case 'disbursed':
          return 'phase-disbursed';
        case 'not taken up':
          return 'phase-not-taken';
        case 'requested':
          return 'phase-requested';
        case 'approved':
          return 'phase-approved';
        case 'rejected':
          return 'phase-rejected';
        default:
          return 'phase-default';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.extracted-details {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 100%;
}

.details-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 1.5rem;
  
  .section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 2rem 0;
    font-family: $brand-font-medium;
    
    i {
      color: #10b981;
      font-size: 1.2rem;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  
  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 0;
    
    .value {
      font-size: 1rem;
      color: #111827;
      font-family: $brand-font-regular;
      word-break: break-word;
      line-height: 1.5;
      font-weight: 400;
    }
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  
  .summary-card {
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
    transition: all 0.2s ease;
    
    &:hover {
      background: #e9ecef;
      transform: translateY(-1px);
    }
    
    &.highlight {
      background: rgba($color-primary, 0.1);
      border-color: $color-primary;
      
      .summary-value {
        color: $color-primary;
        font-weight: 600;
      }
    }
    
    .summary-label {
      font-size: 0.9rem;
      color: #6c757d;
      margin-bottom: 0.5rem;
      font-family: $brand-font-medium;
    }
    
    .summary-value {
      font-size: 1.2rem;
      font-weight: 500;
      color: #2d3748;
      font-family: $brand-font-medium;
    }
  }
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #2d3748;
    font-family: $brand-font-medium;
    font-size: 0.9rem;
  }
  
  td {
    color: #495057;
    font-family: $brand-font-regular;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover {
    background: #f8f9fa;
  }
}

.rate-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.rate-standard {
    background: #d1fae5;
    color: #065f46;
  }
  
  &.rate-zero {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.rate-reverse {
    background: #dbeafe;
    color: #1e40af;
  }
}

.list-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  .list-item {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 1rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 6px;
    align-items: center;
    
    .list-field {
      font-family: $brand-font-regular;
      
      &.description {
        font-weight: 500;
        color: #2d3748;
      }
      
      &.amount {
        text-align: right;
        font-weight: 500;
        color: #2d3748;
      }
      
      &.type {
        text-align: center;
        font-size: 0.9rem;
        color: #6c757d;
        text-transform: capitalize;
      }
    }
  }
}

.text-content {
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem;
  color: #495057;
  line-height: 1.6;
  font-family: $brand-font-regular;
}

.credit-facilities-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  .credit-facility-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    
    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
    
    .facility-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e2e8f0;
      
      .facility-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.1rem;
        font-weight: 600;
        color: #2d3748;
        margin: 0;
        font-family: $brand-font-medium;
        
        i {
          color: $color-primary;
          font-size: 1.2rem;
        }
      }
      
      .facility-status {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        
        &.status-active {
          background: #d1fae5;
          color: #065f46;
        }
        
        &.status-closed {
          background: #f3f4f6;
          color: #6b7280;
        }
      }
    }
    
    .facility-details {
      .details-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
        
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          
          .detail-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-family: $brand-font-medium;
          }
          
          .detail-value {
            font-size: 0.9rem;
            color: #2d3748;
            font-weight: 500;
            word-break: break-word;
            font-family: $brand-font-regular;
          }
        }
      }
    }
  }
}

.credit-applications-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  .credit-application-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    
    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
    
    .application-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e2e8f0;
      
      .application-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.1rem;
        font-weight: 600;
        color: #2d3748;
        margin: 0;
        font-family: $brand-font-medium;
        
        i {
          color: $color-primary;
          font-size: 1.2rem;
        }
      }
      
      .application-phase {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        
        &.phase-disbursed {
          background: #d1fae5;
          color: #065f46;
        }
        
        &.phase-approved {
          background: #dbeafe;
          color: #1e40af;
        }
        
        &.phase-requested {
          background: #fef3c7;
          color: #92400e;
        }
        
        &.phase-not-taken {
          background: #f3f4f6;
          color: #6b7280;
        }
        
        &.phase-rejected {
          background: #fee2e2;
          color: #991b1b;
        }
        
        &.phase-default {
          background: #f3f4f6;
          color: #6b7280;
        }
      }
    }
    
    .application-details {
      .details-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
        
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          
          .detail-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-family: $brand-font-medium;
          }
          
          .detail-value {
            font-size: 0.9rem;
            color: #2d3748;
            font-weight: 500;
            word-break: break-word;
            font-family: $brand-font-regular;
          }
        }
      }
    }
  }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  .no-data-icon {
    font-size: 3rem;
    color: #adb5bd;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #6c757d;
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
    font-family: $brand-font-medium;
  }
  
  p {
    color: #adb5bd;
    margin: 0;
    font-family: $brand-font-regular;
  }
}

@media (max-width: 768px) {
  .details-section {
    padding: 1.5rem;
    margin-bottom: 1rem;
    
    .section-title {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
  }
  
  .info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    
    .info-item {
      padding: 0.5rem 0;
    }
  }
  
  .summary-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
  }
  
  .list-item {
    grid-template-columns: 1fr;
    text-align: left;
    
    .list-field.amount,
    .list-field.type {
      text-align: left;
    }
  }
  
  .table-container {
    font-size: 0.9rem;
  }
}
</style>
